// src/hooks/useOrderForm.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getAllOrderReferenceData } from '../services/api/orderReference';
import { getShops } from '../services/api/shops';
import { lookupOrder } from '../services/api/orders';
import { getDeliveryCharge } from '../services/api/deliveryCharges';
import { calculateOrderTotals, generateOrderNumberPreview, calculatePaymentBalance, getCrossSellMaxQty } from '../utils/orderCalculations';
import { usePermissions } from './usePermissions';
import { useLocalization } from '../contexts/LocalizationContext';
import { useBusinessSettings } from './useBusinessSettings';
import { ORDER_FORM_FIELDS_NAMESPACE, getRequiredOrderFields } from '../utils/orderFormFields';
import { computeDeliveryWithWeight } from './useDeliveryChargeCalculation';
import { getItemMergeKey, explodeBundle, rescaleBundleRows, applyBundleCrossSell } from '../utils/orderItemGrouping';
import { formatPhoneNumber } from '../utils/phoneNumberFormatter';

const AUTOFILL_FIELDS = ['customerName', 'address', 'cityId', 'customerEmail'];

// Resolve the best matching offer for a given quantity.
const resolveOffer = (offers, quantity) => {
  if (!offers || !offers.length) return null;
  const qty = parseInt(quantity) || 1;
  const equalMatch = offers.find(o => o.criteria === 'equal' && parseInt(o.quantity) === qty);
  if (equalMatch) return equalMatch;
  const gteMatches = offers
    .filter(o => o.criteria === 'gte' && qty >= parseInt(o.quantity))
    .sort((a, b) => parseInt(b.quantity) - parseInt(a.quantity));
  return gteMatches[0] || null;
};
// When an offer is flagged applyAsDiscount, the unit price stays at the base price —
// the saving is expressed via resolveOfferLineDiscount() instead.
const resolveOfferPrice = (offers, qty, fallback) => {
  const o = resolveOffer(offers, qty);
  if (!o) return fallback;
  return o.applyAsDiscount ? fallback : parseFloat(o.unitPrice);
};
const resolveOfferLineDiscount = (offers, qty, basePrice) => {
  const o = resolveOffer(offers, qty);
  if (!o || !o.applyAsDiscount) return 0;
  const q = parseInt(qty) || 1;
  return Math.max(0, (parseFloat(basePrice) - parseFloat(o.unitPrice)) * q);
};
const resolveOfferDelivery = (offers, qty) => {
  const o = resolveOffer(offers, qty);
  return (o && o.overrideDeliveryCharge != null) ? parseFloat(o.overrideDeliveryCharge) : null;
};

// Calculate total weight from order items.
// Items without a defined weight are treated as 0.
// If ALL items lack a defined weight, returns 1.
const calculateWeightFromItems = (items) => {
  if (!items || items.length === 0) return 1;
  const withWeight = items.filter(i => i.weight != null && parseFloat(i.weight) > 0);
  if (withWeight.length === 0) return 1;
  return withWeight.reduce((sum, i) => sum + parseFloat(i.weight) * i.quantity, 0);
};

/**
 * @param {object|null} initialData - Reserved for future use; pass null always.
 * @param {object} options
 * @param {boolean} options.disableDeliveryChargeAutoCalc - When true, suppresses ALL
 *   automatic delivery charge recalculation (used by EditOrderPage).
 * @param {number|null} options.currentOrderId - When editing an existing replacement order, its
 *   own id, so the replaced-order lookup excludes this order's own already-claimed return
 *   quantities from "remaining" (used by EditOrderPage).
 */
const useOrderForm = (initialData = null, { disableDeliveryChargeAutoCalc = false, currentOrderId = null } = {}) => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { settings: orderFormFieldSettings } = useBusinessSettings(ORDER_FORM_FIELDS_NAMESPACE);
  const requiredFields = useMemo(() => getRequiredOrderFields(orderFormFieldSettings), [orderFormFieldSettings]);

  // Form state
  const [formData, setFormData] = useState({
    shopId: null,
    customerName: '',
    privateReference: '',
    address: '',
    cityId: null,
    phoneNumbers: [{ phoneNumber: '', isPrimary: true }],
    customerEmail: '',
    deliveryNotes: '',
    specialNotes: '',
    orderStatusKey: 'pending',
    orderStatusReasonKey: null,
    holdUntilDate: null,
    paymentTypeKey: 'cash_on_delivery',
    replacedOrderLookupValue: '',
    replacedOrderId: null,
    replacementReasonKey: null,
    replacementCodAmount: 0,
    returnAirwaybillNumber: '',
    returnItems: [],
    orderItems: [],
    deliveryCharge: 0,
    weight: 1,
    paymentRecords: [],
    orderOriginKey: null,
    messageCenterShiftId: null,
    createdByUserId: null,
  });

  // Replacement order lookup state
  const [replacedOrderData, setReplacedOrderData] = useState(null);
  const [isLookingUpOrder, setIsLookingUpOrder] = useState(false);
  const [orderLookupError, setOrderLookupError] = useState(null);
  const lookupTimeoutRef = useRef(null);

  // Reference data
  const [referenceData, setReferenceData] = useState({
    orderStatuses: [],
    orderStatusReasons: {},
    paymentTypes: [],
    paymentMethods: [],
    replacementReasons: [],
    cities: []
  });

  // User shops
  const [userShops, setUserShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);

  // Phone validation state
  const [phoneValidationErrors, setPhoneValidationErrors] = useState({});

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [orderTotals, setOrderTotals] = useState({
    totalAmount: 0,
    totalDiscount: 0,
    totalProductCost: 0,
    totalTax: 0,
    deliveryCharge: 0,
    grandTotal: 0
  });

  // Permission checks
  const canChangeUnitPrice = hasPermission('change_unit_price');
  const canGiveDiscount = hasPermission('give_discount');
  const canChangeDeliveryCharge = hasPermission('change_delivery_charge');

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);

        const [referenceResponse, shopsResponse] = await Promise.all([
          getAllOrderReferenceData(),
          getShops({ assigned: true, type: 'online,dropshipper' })
        ]);

        setReferenceData({
          ...referenceResponse,
          orderStatuses: referenceResponse.orderStatuses || [],
        });
        setUserShops(shopsResponse || []);

        const savedShopId = sessionStorage.getItem('selectedShopId');
        const defaultShop = shopsResponse?.find(shop =>
          savedShopId ? shop.id === parseInt(savedShopId) : shop.isDefault
        ) || shopsResponse?.[0];

        if (defaultShop) {
          setSelectedShop(defaultShop);
          setFormData(prev => ({ ...prev, shopId: defaultShop.id }));
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Sync selectedShop when formData.shopId changes externally (e.g. EditOrderPage calling setFormData)
  useEffect(() => {
    if (!formData.shopId || userShops.length === 0) return;
    const shop = userShops.find(s => s.id === formData.shopId);
    if (shop) setSelectedShop(shop);
  }, [formData.shopId, userShops]);

  // Calculate totals when order items or delivery charge changes
  useEffect(() => {
    const totals = calculateOrderTotals(formData.orderItems, formData.deliveryCharge);
    setOrderTotals(totals);
  }, [formData.orderItems, formData.deliveryCharge]);

  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  // Tracks the last API-fetched base delivery charge / extraChargePerKg so the override
  // effect can compare against it without triggering extra API calls.
  const baseDeliveryChargeRef = useRef(null);
  const extraChargePerKgRef = useRef(null);

  // ── Effect 1: Fetch base delivery charge from API when shop or city changes ──
  // Runs in both create and edit mode to populate refs for weight-based recalc.
  // In edit mode, refs are populated but delivery charge state is NOT overwritten.
  // Also skipped when override items are already present (no point fetching if
  // the override will win anyway).
  useEffect(() => {
    const fetchBaseDeliveryCharge = async () => {
      if (!formData.shopId || !formData.cityId) return;

      // Store pickup orders are never delivered — force delivery charge to 0
      // and skip the zone-based lookup entirely.
      if (formData.paymentTypeKey === 'store_pickup') {
        setFormData(prev => prev.deliveryCharge === 0 ? prev : { ...prev, deliveryCharge: 0 });
        return;
      }

      // If any current item already carries an override, skip the API call —
      // Effect 2 will set the correct value from the override.
      const hasOverride = formData.orderItems.some(
        item => item.overrideDeliveryCharge != null
      );
      if (hasOverride) return;

      try {
        setIsCalculatingDelivery(true);
        const response = await getDeliveryCharge(formData.shopId, formData.cityId);
        const { amount, extraChargePerKg = 0 } = response.deliveryCharge;
        baseDeliveryChargeRef.current = amount;
        extraChargePerKgRef.current = extraChargePerKg;
        if (!disableDeliveryChargeAutoCalc) {
          const computed = computeDeliveryWithWeight(amount, extraChargePerKg, formData.weight);
          setFormData(prev => ({ ...prev, deliveryCharge: computed }));
        }
      } catch (error) {
        console.error('Failed to calculate delivery charge:', error);
        baseDeliveryChargeRef.current = 0;
        extraChargePerKgRef.current = 0;
        if (!disableDeliveryChargeAutoCalc) {
          setFormData(prev => ({ ...prev, deliveryCharge: 0 }));
        }
      } finally {
        setIsCalculatingDelivery(false);
      }
    };

    fetchBaseDeliveryCharge();
  // formData.orderItems intentionally excluded — handled by Effect 2
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.shopId, formData.cityId, formData.paymentTypeKey, disableDeliveryChargeAutoCalc]);

  // ── Effect 2: Apply / remove product-level delivery charge override ──
  // Runs when order items or weight changes. No API calls — pure derivation from item data.
  // In edit mode, item-override logic is skipped but weight-based recalc still runs.
  useEffect(() => {
    // Wait until city/shop are set (base charge is meaningful)
    if (!formData.shopId || !formData.cityId) return;

    // Store pickup orders are never delivered — force delivery charge to 0,
    // overriding any offer/shop delivery override or weight-based recalculation.
    if (formData.paymentTypeKey === 'store_pickup') {
      setFormData(prev => prev.deliveryCharge === 0 ? prev : { ...prev, deliveryCharge: 0 });
      return;
    }

    // Offer-based overrides: apply in both create and edit mode so that
    // quantity changes that cross an offer threshold update delivery charge.
    // Only items with an actively-resolved offerDeliveryOverride (set by
    // updateOrderItem / addOrderItem) contribute — initial-load items have
    // offerDeliveryOverride = undefined so they don't interfere.
    const offerOverrides = formData.orderItems
      .map(item => (item.offerDeliveryOverride != null ? parseFloat(item.offerDeliveryOverride) : null))
      .filter(v => v !== null);
    if (offerOverrides.length > 0) {
      const lowest = Math.min(...offerOverrides);
      setFormData(prev => prev.deliveryCharge === lowest ? prev : { ...prev, deliveryCharge: lowest });
      return;
    }

    if (!disableDeliveryChargeAutoCalc) {
      // Shop-level overrides as fallback (use highest) — create mode only
      const shopOverrides = formData.orderItems
        .map(item => (item.overrideDeliveryCharge != null ? parseFloat(item.overrideDeliveryCharge) : null))
        .filter(v => v !== null);
      if (shopOverrides.length > 0) {
        const highest = Math.max(...shopOverrides);
        setFormData(prev => {
          if (prev.deliveryCharge === highest) return prev;
          return { ...prev, deliveryCharge: highest };
        });
        return;
      }
    }

    // Restore/recalculate base charge with weight-based extra.
    // Runs in both create and edit mode.
    const base = baseDeliveryChargeRef.current;
    const extraPerKg = extraChargePerKgRef.current;
    if (base !== null) {
      const computed = computeDeliveryWithWeight(base, extraPerKg, formData.weight);
      setFormData(prev => {
        if (prev.deliveryCharge === computed) return prev;
        return { ...prev, deliveryCharge: computed };
      });
    }
    // If base is still null (city/shop not yet resolved), Effect 1 will set it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.orderItems, formData.weight, formData.paymentTypeKey, disableDeliveryChargeAutoCalc]);

  // Order lookup with debouncing
  useEffect(() => {
    const performLookup = async () => {
      const lookupValue = formData.replacedOrderLookupValue?.trim();

      if (!lookupValue) {
        setReplacedOrderData(null);
        setOrderLookupError(null);
        setFormData(prev => ({ ...prev, replacedOrderId: null, returnItems: [] }));
        return;
      }

      try {
        setIsLookingUpOrder(true);
        setOrderLookupError(null);

        const response = await lookupOrder(lookupValue, currentOrderId);

        if (response.order) {
          setReplacedOrderData(response.order);
          const returnItems = (response.order.orderItems || []).map(item => ({
            replacedOrderItemId: item.id,
            productName: item.Product?.name,
            variationSku: item.ProductVariation?.sku,
            originalQuantity: parseFloat(item.quantity),
            remainingQty: parseFloat(item.remainingReturnableQuantity),
            // Prefill to the remaining qty only when creating; on edit keep what this order already claimed
            returnQty: currentOrderId
              ? (parseFloat(item.alreadyClaimedByThisOrder) || 0)
              : parseFloat(item.remainingReturnableQuantity),
          }));
          setFormData(prev => ({ ...prev, replacedOrderId: response.order.id, returnItems }));
          setOrderLookupError(null);
        }
      } catch (error) {
        console.error('Order lookup error:', error);
        setReplacedOrderData(null);
        setFormData(prev => ({ ...prev, replacedOrderId: null, returnItems: [] }));

        if (error.response?.status === 404) {
          setOrderLookupError('Order not found');
        } else {
          setOrderLookupError('Failed to lookup order');
        }
      } finally {
        setIsLookingUpOrder(false);
      }
    };

    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }

    if (formData.replacedOrderLookupValue?.trim()) {
      lookupTimeoutRef.current = setTimeout(performLookup, 500);
    } else {
      setReplacedOrderData(null);
      setOrderLookupError(null);
      setFormData(prev => ({ ...prev, replacedOrderId: null, returnItems: [] }));
    }

    return () => {
      if (lookupTimeoutRef.current) {
        clearTimeout(lookupTimeoutRef.current);
      }
    };
  }, [formData.replacedOrderLookupValue, currentOrderId]);

  // Handle shop selection
  const handleShopChange = useCallback((shopId) => {
    const shop = userShops.find(s => s.id === shopId);
    if (shop) {
      setSelectedShop(shop);
      setFormData(prev => ({ ...prev, shopId }));
      sessionStorage.setItem('selectedShopId', shopId.toString());
    }
  }, [userShops]);

  // Handle form field changes
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'paymentTypeKey') {
        const paymentType = referenceData.paymentTypes?.find(p => p.key === value);
        if (paymentType && !paymentType.requiresPaymentRecord && !paymentType.allowsOptionalPaymentRecord) {
          next.paymentRecords = (prev.paymentRecords || []).filter(r => r.isVerified);
        }
      }
      return next;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors, referenceData.paymentTypes]);

  // Handle phone number changes
  const handlePhoneNumberChange = useCallback((index, value) => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) =>
        i === index ? { ...phone, phoneNumber: value } : phone
      )
    }));
  }, []);

  // Handle phone validation changes
  const handlePhoneValidationChange = useCallback((index, hasError, errorMessage) => {
    setPhoneValidationErrors(prev => {
      const newErrors = { ...prev };
      if (hasError) {
        newErrors[index] = errorMessage;
      } else {
        delete newErrors[index];
      }
      return newErrors;
    });
  }, []);

  const togglePrimaryPhone = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) => ({
        ...phone,
        isPrimary: i === index
      }))
    }));
  }, []);

  const addPhoneNumber = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, { phoneNumber: '', isPrimary: false }]
    }));
  }, []);

  // Values the last autofill applied, so a phone change can clear the ones the user hasn't touched since
  const autofillSnapshotRef = useRef(null);

  // Autofill customer details from phone lookup result
  const autofillCustomer = useCallback(({ name, address, cityId, email, additionalPhones }) => {
    setFormData(prev => {
      const firstPhone = prev.phoneNumbers[0] || { phoneNumber: '', isPrimary: true };
      const extraPhones = additionalPhones || [];
      const next = {
        ...prev,
        customerName: name || prev.customerName,
        address: address || prev.address,
        cityId: cityId || prev.cityId,
        customerEmail: email || prev.customerEmail,
        phoneNumbers: [
          { ...firstPhone, isPrimary: true },
          ...extraPhones.map(p => ({ phoneNumber: p, isPrimary: false })),
        ],
      };
      autofillSnapshotRef.current = {
        applied: {
          ...Object.fromEntries(AUTOFILL_FIELDS.map(field => [field, next[field]])),
          extraPhones,
        },
      };
      return next;
    });
  }, []);

  const revertAutofill = useCallback(() => {
    const snapshot = autofillSnapshotRef.current;
    if (!snapshot) return;
    autofillSnapshotRef.current = null;
    setFormData(prev => {
      const next = { ...prev };
      AUTOFILL_FIELDS.forEach(field => {
        if (prev[field] === snapshot.applied[field]) next[field] = field === 'cityId' ? null : '';
      });
      const currentExtras = prev.phoneNumbers.slice(1).map(p => p.phoneNumber);
      if (currentExtras.length > 0 && JSON.stringify(currentExtras) === JSON.stringify(snapshot.applied.extraPhones)) {
        next.phoneNumbers = [prev.phoneNumbers[0]];
      }
      return next;
    });
  }, []);

  const clearOrderItems = useCallback(() => {
    setFormData(prev => ({ ...prev, orderItems: [], weight: calculateWeightFromItems([]) }));
  }, []);

  const removePhoneNumber = useCallback((index) => {
    setFormData(prev => {
      const newPhoneNumbers = prev.phoneNumbers.filter((_, i) => i !== index);
      if (prev.phoneNumbers[index]?.isPrimary && newPhoneNumbers.length > 0) {
        newPhoneNumbers[0].isPrimary = true;
      }
      return { ...prev, phoneNumbers: newPhoneNumbers };
    });

    setPhoneValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      const reindexedErrors = {};
      Object.keys(newErrors).forEach(key => {
        const errorIndex = parseInt(key);
        if (errorIndex > index) {
          reindexedErrors[errorIndex - 1] = newErrors[key];
        } else {
          reindexedErrors[key] = newErrors[key];
        }
      });
      return reindexedErrors;
    });
  }, []);

  // Handle order items — overrideDeliveryCharge is stored on the item
  // so Effect 2 can derive the correct delivery charge automatically.
  const addOrderItem = useCallback((product) => {
    setFormData(prev => {
      if (product.type === 'bundle') {
        const existingGroup = prev.orderItems.find(item => item.bundleGroupKey && item.bundleProductId === product.productId);
        const newItems = existingGroup
          ? rescaleBundleRows(prev.orderItems, existingGroup.bundleGroupKey, (Number(existingGroup.bundleQuantity) || 0) + 1)
          : [...prev.orderItems, ...explodeBundle(product)];
        return { ...prev, orderItems: newItems, weight: calculateWeightFromItems(newItems) };
      }
      const productKey = getItemMergeKey(product);
      const existingItemIndex = prev.orderItems.findIndex(item => getItemMergeKey(item) === productKey);

      let newItems;
      if (existingItemIndex !== -1) {
        newItems = [...prev.orderItems];
        const existing = newItems[existingItemIndex];
        const newQty = existing.quantity + 1;
        newItems[existingItemIndex] = {
          ...existing,
          quantity: newQty,
          unitPrice: existing.manuallyPriced
            ? existing.unitPrice
            : resolveOfferPrice(existing.offers, newQty, existing.originalUnitPrice),
          lineDiscount: existing.manuallyDiscounted
            ? existing.lineDiscount
            : resolveOfferLineDiscount(existing.offers, newQty, existing.originalUnitPrice),
          offerDeliveryOverride: resolveOfferDelivery(existing.offers, newQty),
          crossSellQuantity: existing.crossSellQuantity != null
            ? Math.min(existing.crossSellQuantity, newQty)
            : existing.crossSellQuantity,
        };
      } else {
        const basePrice = product.price;
        const newItem = {
          id: Date.now(),
          productId: product.productId,
          variationId: product.variationId || null,
          displayName: product.displayName,
          sku: product.sku,
          quantity: 1,
          unitPrice: resolveOfferPrice(product.offers, 1, basePrice),
          originalUnitPrice: basePrice,
          manuallyPriced: false,
          lineDiscount: resolveOfferLineDiscount(product.offers, 1, basePrice),
          manuallyDiscounted: false,
          overrideDeliveryCharge: product.overrideDeliveryCharge ?? null,
          offerDeliveryOverride: resolveOfferDelivery(product.offers, 1),
          offers: product.offers || [],
          weight: product.weight ?? null,
          isCrossSell: false,
          crossSellQuantity: null,
          crossSellById: null,
          baselineQuantity: null,
          originalQuantity: null,
        };
        newItems = [...prev.orderItems, newItem];
      }
      return { ...prev, orderItems: newItems, weight: calculateWeightFromItems(newItems) };
    });
  }, []);

  const updateOrderItem = useCallback((itemId, field, value, meta = {}) => {
    setFormData(prev => {
      const newItems = prev.orderItems.map(item => {
        if (item.id !== itemId) return item;
        if (item.bundleGroupKey && (field === 'unitPrice' || field === 'lineDiscount')) return item;
        const updated = { ...item, [field]: value };
        if (field === 'quantity') {
          const newQty = parseInt(value) || 1;
          if (!item.manuallyPriced) {
            updated.unitPrice = resolveOfferPrice(item.offers, newQty, item.originalUnitPrice);
          }
          if (!item.manuallyDiscounted) {
            updated.lineDiscount = resolveOfferLineDiscount(item.offers, newQty, item.originalUnitPrice);
          }
          updated.offerDeliveryOverride = resolveOfferDelivery(item.offers, newQty);
          if (item.isCrossSell && item.crossSellQuantity > newQty) {
            updated.crossSellQuantity = newQty;
          }
          // If shrinking the quantity drops eligibility to zero, auto-clear the
          // cross-sell flag (but keep the remembered number — see isCrossSell branch).
          if (item.isCrossSell && (meta.currentUserId !== undefined || meta.createdByUserId !== undefined)) {
            const recomputedCap = getCrossSellMaxQty({ ...item, quantity: newQty }, meta);
            if (recomputedCap <= 0) {
              updated.isCrossSell = false;
            }
          }
        } else if (field === 'unitPrice') {
          updated.manuallyPriced = true;
        } else if (field === 'lineDiscount') {
          updated.manuallyDiscounted = true;
        } else if (field === 'isCrossSell') {
          const cap = meta.maxCrossSellQty ?? item.quantity;
          // Untick: keep the remembered quantity in memory. Re-tick: reuse the
          // remembered value (clamped into range) if one exists, otherwise default
          // to the max eligible amount, same as a first-time tick.
          const defaultQty = item.crossSellQuantity != null ? item.crossSellQuantity : Math.min(item.quantity, cap);
          updated.crossSellQuantity = value
            ? Math.max(1, Math.min(defaultQty, item.quantity, cap))
            : item.crossSellQuantity;
        } else if (field === 'crossSellQuantity') {
          const cap = meta.maxCrossSellQty ?? item.quantity;
          updated.crossSellQuantity = Math.min(Math.max(parseInt(value) || 1, 1), cap);
        }
        return updated;
      });
      return { ...prev, orderItems: newItems, weight: calculateWeightFromItems(newItems) };
    });
  }, []);

  const removeOrderItem = useCallback((itemId) => {
    setFormData(prev => {
      const newItems = prev.orderItems.filter(item => item.id !== itemId);
      return { ...prev, orderItems: newItems, weight: calculateWeightFromItems(newItems) };
    });
  }, []);

  const updateBundleQuantity = useCallback((groupKey, qty) => {
    const newQty = parseInt(qty);
    if (!newQty || newQty < 1) return;
    setFormData(prev => {
      const newItems = rescaleBundleRows(prev.orderItems, groupKey, newQty);
      return { ...prev, orderItems: newItems, weight: calculateWeightFromItems(newItems) };
    });
  }, []);

  const removeBundle = useCallback((groupKey) => {
    setFormData(prev => {
      const newItems = prev.orderItems.filter(item => item.bundleGroupKey !== groupKey);
      return { ...prev, orderItems: newItems, weight: calculateWeightFromItems(newItems) };
    });
  }, []);

  const setBundleCrossSell = useCallback((groupKey, enabled, bundlesN) => {
    setFormData(prev => ({
      ...prev,
      orderItems: applyBundleCrossSell(prev.orderItems, groupKey, enabled, Math.max(1, parseInt(bundlesN) || 1)),
    }));
  }, []);

  // Handle weight change (manual user edit)
  const handleWeightChange = useCallback((value) => {
    const num = parseFloat(value);
    setFormData(prev => ({ ...prev, weight: isNaN(num) ? 1 : Math.max(0, num) }));
  }, []);

  // Handle return-item quantity changes for replacement orders
  const updateReturnItemQty = useCallback((replacedOrderItemId, qty) => {
    setFormData(prev => ({
      ...prev,
      returnItems: prev.returnItems.map(item => {
        if (item.replacedOrderItemId !== replacedOrderItemId) return item;
        const clamped = Math.min(Math.max(0, parseFloat(qty) || 0), item.remainingQty);
        return { ...item, returnQty: clamped };
      })
    }));
  }, []);

  // Handle payment records
  const addPaymentRecord = useCallback((paymentMethodKey) => {
    setFormData(prev => {
      const balance = calculatePaymentBalance(orderTotals.grandTotal, prev.paymentRecords);
      const newRecord = {
        id: Date.now(),
        paymentMethodKey: paymentMethodKey || '',
        paymentAmount: balance.isFullyPaid ? '' : balance.balance,
        reference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        proofUrl: null
      };
      return {
        ...prev,
        paymentRecords: [...prev.paymentRecords, newRecord]
      };
    });
  }, [orderTotals.grandTotal]);

  const updatePaymentRecord = useCallback((recordId, field, value) => {
    setFormData(prev => ({
      ...prev,
      paymentRecords: prev.paymentRecords.map(record =>
        record.id === recordId ? { ...record, [field]: value } : record
      )
    }));
  }, []);

  const removePaymentRecord = useCallback((recordId) => {
    setFormData(prev => ({
      ...prev,
      paymentRecords: prev.paymentRecords.filter(record => record.id !== recordId)
    }));
  }, []);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.shopId) newErrors.shopId = t('ecomOrders.validation.shopRequired');
    if (!formData.customerName.trim()) newErrors.customerName = t('ecomOrders.validation.customerNameRequired');
    if (!formData.address.trim()) newErrors.address = t('ecomOrders.validation.addressRequired');
    if (!formData.cityId) newErrors.cityId = t('ecomOrders.validation.cityRequired');

    const validPhoneNumbers = formData.phoneNumbers.filter(p => p.phoneNumber.trim());
    if (validPhoneNumbers.length === 0) {
      newErrors.phoneNumbers = t('ecomOrders.validation.phoneRequired');
    } else {
      const hasPrimary = validPhoneNumbers.some(p => p.isPrimary);
      if (!hasPrimary) newErrors.phoneNumbers = t('ecomOrders.validation.primaryPhoneRequired');

      const phoneErrors = [];
      const seenPhones = new Set();
      formData.phoneNumbers.forEach((phone, index) => {
        if (phone.phoneNumber.trim() && phoneValidationErrors[index]) {
          phoneErrors[index] = phoneValidationErrors[index];
          return;
        }
        const formatted = formatPhoneNumber(phone.phoneNumber);
        if (!formatted.isValid) return;
        if (seenPhones.has(formatted.formattedNumber)) {
          phoneErrors[index] = t('ecomOrders.validation.duplicatePhone');
        }
        seenPhones.add(formatted.formattedNumber);
      });
      if (phoneErrors.length > 0) newErrors.phoneNumberDetails = phoneErrors;
    }

    const isBlank = (value) => !value || !String(value).trim();
    if (requiredFields.privateReference && isBlank(formData.privateReference)) newErrors.privateReference = t('ecomOrders.validation.privateReferenceRequired');
    if (requiredFields.customerEmail && isBlank(formData.customerEmail)) newErrors.customerEmail = t('ecomOrders.validation.customerEmailRequired');
    if (requiredFields.deliveryNotes && isBlank(formData.deliveryNotes)) newErrors.deliveryNotes = t('ecomOrders.validation.deliveryNotesRequired');
    if (requiredFields.specialNotes && isBlank(formData.specialNotes)) newErrors.specialNotes = t('ecomOrders.validation.specialNotesRequired');
    if (requiredFields.orderOriginKey && !formData.orderOriginKey) newErrors.orderOriginKey = t('ecomOrders.validation.orderOriginRequired');

    if (formData.customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customerEmail)) {
        newErrors.customerEmail = t('ecomOrders.validation.invalidEmail');
      }
    }

    const selectedPaymentType = referenceData.paymentTypes.find(p => p.key === formData.paymentTypeKey);

    if (formData.orderItems.length === 0) {
      newErrors.orderItems = t('ecomOrders.validation.orderItemsRequired');
    } else {
      const itemErrors = [];
      formData.orderItems.forEach((item, index) => {
        const itemError = {};
        if (!item.quantity || item.quantity <= 0) itemError.quantity = t('ecomOrders.validation.quantityInvalid');
        if (!item.bundleGroupKey && (!item.unitPrice || item.unitPrice <= 0)) itemError.unitPrice = t('ecomOrders.validation.unitPriceInvalid');
        if (item.lineDiscount && item.lineDiscount < 0) itemError.lineDiscount = t('ecomOrders.validation.discountNegative');
        else if (!selectedPaymentType?.isReplacement && !item.bundleGroupKey && (parseFloat(item.lineDiscount) || 0) > (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0) + 0.005) {
          itemError.lineDiscount = t('ecomOrders.validation.discountExceedsLine');
        }
        if (Object.keys(itemError).length > 0) itemErrors[index] = itemError;
      });
      if (itemErrors.length > 0) newErrors.orderItemDetails = itemErrors;
    }

    if ((parseFloat(formData.deliveryCharge) || 0) < 0) newErrors.deliveryCharge = t('ecomOrders.validation.deliveryChargeNegative');

    if (!formData.orderStatusKey) newErrors.orderStatusKey = t('ecomOrders.validation.orderStatusRequired');
    if (!formData.paymentTypeKey) newErrors.paymentTypeKey = t('ecomOrders.validation.paymentTypeRequired');

    const selectedStatus = referenceData.orderStatuses.find(s => s.key === formData.orderStatusKey);
    if (selectedStatus?.requiresReason && !formData.orderStatusReasonKey) {
      newErrors.orderStatusReasonKey = t('ecomOrders.validation.statusReasonRequired');
    }
    if (selectedStatus?.requiresHoldDate && !formData.holdUntilDate) {
      newErrors.holdUntilDate = t('ecomOrders.validation.holdDateRequired');
    }

    if (selectedPaymentType?.isReplacement) {
      if (!formData.replacedOrderLookupValue?.trim()) {
        newErrors.replacedOrderLookupValue = t('ecomOrders.validation.replacedOrderRequired');
      } else {
        if (isLookingUpOrder) {
          newErrors.replacedOrderLookupValue = t('ecomOrders.validation.orderLookupPending');
        } else if (!formData.replacedOrderId || orderLookupError) {
          newErrors.replacedOrderLookupValue = orderLookupError || t('ecomOrders.validation.validReplacedOrderRequired');
        }
      }

      if (!formData.replacementReasonKey) newErrors.replacementReasonKey = t('ecomOrders.validation.replacementReasonRequired');

      if (formData.replacementCodAmount === null || formData.replacementCodAmount === undefined || formData.replacementCodAmount === '') {
        newErrors.replacementCodAmount = t('ecomOrders.validation.replacementCodRequired');
      } else {
        const codAmount = parseFloat(formData.replacementCodAmount);
        if (isNaN(codAmount) || codAmount < 0) {
          newErrors.replacementCodAmount = t('ecomOrders.validation.replacementCodNegative');
        } else if (codAmount > orderTotals.grandTotal) {
          newErrors.replacementCodAmount = t('ecomOrders.validation.replacementCodExceeds');
        }
      }

      const overReturnedItem = (formData.returnItems || []).find(item => item.returnQty > item.remainingQty);
      if (overReturnedItem) {
        newErrors.returnItems = t('ecomOrders.validation.returnQtyExceeds');
      }
    }

    if (selectedPaymentType?.requiresPaymentRecord && formData.paymentRecords.length === 0) {
      newErrors.paymentRecords = t('ecomOrders.validation.paymentRecordsRequired');
    } else if (formData.paymentRecords.length > 0) {
      const recordErrors = [];
      formData.paymentRecords.forEach((record, index) => {
        const recordError = {};
        if (!record.paymentMethodKey) recordError.paymentMethodKey = t('ecomOrders.validation.paymentMethodRequired');
        if (!record.paymentAmount || record.paymentAmount <= 0) recordError.paymentAmount = t('ecomOrders.validation.paymentAmountInvalid');
        if (!record.paymentDate) recordError.paymentDate = t('ecomOrders.validation.paymentDateRequired');
        if (Object.keys(recordError).length > 0) recordErrors[index] = recordError;
      });
      if (recordErrors.length > 0) newErrors.paymentRecordDetails = recordErrors;

      if (recordErrors.length === 0 && selectedPaymentType?.requiresPaymentRecord) {
        const paymentBalance = calculatePaymentBalance(orderTotals.grandTotal, formData.paymentRecords);
        const totalPaid = paymentBalance.totalPaid;
        const grandTotal = orderTotals.grandTotal;

        if (formData.paymentTypeKey === 'fully_paid' || formData.paymentTypeKey === 'store_pickup') {
          if (Math.abs(totalPaid - grandTotal) > 0.01) {
            if (totalPaid < grandTotal) {
              newErrors.paymentRecords = t('ecomOrders.validation.fullyPaidIncomplete', { totalPaid: formatAmount(totalPaid), grandTotal: formatAmount(grandTotal) });
            } else {
              newErrors.paymentRecords = t('ecomOrders.validation.fullyPaidOverpaid', { totalPaid: formatAmount(totalPaid), grandTotal: formatAmount(grandTotal) });
            }
          }
        } else if (formData.paymentTypeKey === 'partially_paid') {
          if (totalPaid >= grandTotal) {
            newErrors.paymentRecords = t('ecomOrders.validation.partiallyPaidInvalid', { totalPaid: formatAmount(totalPaid), grandTotal: formatAmount(grandTotal) });
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, referenceData.orderStatuses, referenceData.paymentTypes, phoneValidationErrors, isLookingUpOrder, orderLookupError, orderTotals.grandTotal, requiredFields, t]);

  const getOrderNumberPreview = useCallback(() => {
    if (selectedShop?.orderNumberPrefix) {
      return generateOrderNumberPreview(selectedShop.orderNumberPrefix);
    }
    return '';
  }, [selectedShop]);

  const requiresPaymentRecords = useCallback(() => {
    const selectedPaymentType = referenceData.paymentTypes.find(p => p.key === formData.paymentTypeKey);
    return selectedPaymentType?.requiresPaymentRecord || false;
  }, [formData.paymentTypeKey, referenceData.paymentTypes]);

  const allowsOptionalPaymentRecords = useCallback(() => {
    const selectedPaymentType = referenceData.paymentTypes.find(p => p.key === formData.paymentTypeKey);
    return selectedPaymentType?.allowsOptionalPaymentRecord || false;
  }, [formData.paymentTypeKey, referenceData.paymentTypes]);

  const isReplacementOrder = useCallback(() => {
    const selectedPaymentType = referenceData.paymentTypes.find(p => p.key === formData.paymentTypeKey);
    return selectedPaymentType?.isReplacement || false;
  }, [formData.paymentTypeKey, referenceData.paymentTypes]);

  const isStorePickupOrder = useCallback(() => {
    return formData.paymentTypeKey === 'store_pickup';
  }, [formData.paymentTypeKey]);

  const getStatusReasons = useCallback(() => {
    return referenceData.orderStatusReasons[formData.orderStatusKey] || [];
  }, [formData.orderStatusKey, referenceData.orderStatusReasons]);

  // In-packing / dispatched statuses need the matching edit permission; the order's current status always stays listed
  const canSetInPacking = hasPermission('edit_in_packing_order');
  const canSetDispatched = hasPermission('edit_dispatched_order');
  const selectableOrderStatuses = useMemo(() => (referenceData.orderStatuses || []).filter((status) => (
    status.key === formData.orderStatusKey || (
      (!status.isInPacking || canSetInPacking) &&
      (!status.isDispatched || canSetDispatched)
    )
  )), [referenceData.orderStatuses, formData.orderStatusKey, canSetInPacking, canSetDispatched]);

  return {
    formData,
    requiredFields,
    referenceData,
    selectableOrderStatuses,
    userShops,
    selectedShop,
    isLoading,
    isCalculatingDelivery,
    errors,
    orderTotals,
    replacedOrderData,
    isLookingUpOrder,
    orderLookupError,
    canChangeUnitPrice,
    canGiveDiscount,
    canChangeDeliveryCharge,
    handleShopChange,
    handleFieldChange,
    handlePhoneNumberChange,
    handlePhoneValidationChange,
    handleWeightChange,
    togglePrimaryPhone,
    addPhoneNumber,
    removePhoneNumber,
    autofillCustomer,
    revertAutofill,
    clearOrderItems,
    addOrderItem,
    updateOrderItem,
    removeOrderItem,
    updateBundleQuantity,
    removeBundle,
    setBundleCrossSell,
    updateReturnItemQty,
    addPaymentRecord,
    updatePaymentRecord,
    removePaymentRecord,
    validateForm,
    getOrderNumberPreview,
    requiresPaymentRecords,
    allowsOptionalPaymentRecords,
    isReplacementOrder,
    isStorePickupOrder,
    getStatusReasons,
    setFormData,
    setErrors,
    setReplacedOrderData
  };
};

function formatAmount(amount) {
  return `Rs. ${parseFloat(amount).toFixed(2)}`;
}

export default useOrderForm;